import { memo, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ReferenceType } from '@floating-ui/react';
import { ErrorBoundary } from '@frontend/error-boundary';
import {
  SoftphonePopout,
  useSoftphoneWidgetControl,
  useSoftphoneCallState,
  SoftphoneIconButton,
  SoftphoneFeedbackCallRating,
  CallBars,
} from '@frontend/softphone2';
import { useSoftphoneInnerManager } from '@frontend/weave-softphone-manager';
import { theme } from '@frontend/theme';
import { useTooltip } from '@frontend/design-system';

type Props = {
  allowTrayView: boolean;
  allowButtonView: boolean;
  allowPopout: boolean;
};

const DEFAULT_ASK_FOR_FEEDBACK_TIMEOUT = 1500;
const DEFAULT_DISMISS_FEEDBACK_TIMEOUT = 15000;
const MAX_RANGE = 20;
const MIN_RANGE = 10;
/**
 * If under test, just set to 0
 */
const initalGenerateRandomCallCount =
  import.meta.env.MODE === 'test' ? 0 : Math.round(Math.random() * (MAX_RANGE - MIN_RANGE) + MIN_RANGE);

export const SoftphoneWrap = memo(({ allowTrayView, allowButtonView, allowPopout }: Props) => {
  const isLoading = useSoftphoneInnerManager((ctx) => ctx?.isLoading);
  const providersError = useSoftphoneInnerManager((ctx) => ctx?.error);
  const isSoftphoneActive = useSoftphoneInnerManager((ctx) => ctx?.isConnected);
  const isOpen = useSoftphoneWidgetControl((ctx) => ctx?.isOpen);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx?.establishedCalls);
  const callCount = localStorage.getItem('softphone.call-count');
  const parsedCallCount = !!callCount ? +callCount : 0;
  const [randomCallCount, setRandomCallCount] = useState<number>(initalGenerateRandomCallCount);
  const isFeedbackOpened = useSoftphoneWidgetControl((ctx) => ctx?.isFeedbackOpen);
  const setIsFeedbackOpen = useSoftphoneWidgetControl((ctx) => ctx?.setIsFeedbackOpen);
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Store the timeout ID
  const [clearDismissTimeout, setClearDismissTimeout] = useState<boolean>(false);
  const primaryCall = useSoftphoneCallState((ctx) => ctx?.primaryCall);
  const isMobile = useSoftphoneWidgetControl((ctx) => ctx?.isMobile);
  const incomingCalls = useSoftphoneCallState((ctx) => ctx?.incomingCalls);
  const showActiveCallBar = !!primaryCall;
  const showIncomingCallsUI = incomingCalls?.length > 0;
  const showSoftphoneIcon = (allowButtonView && !showActiveCallBar && !showIncomingCallsUI) || !allowTrayView;
  const isConnected = useSoftphoneInnerManager((ctx) => ctx?.isConnected);

  useEffect(() => {
    if (!callCount) {
      localStorage.setItem('softphone.call-count', JSON.stringify(0));
    }
  }, [callCount]);

  const allowCallBarToShow = allowTrayView && !providersError && (showActiveCallBar || showIncomingCallsUI);

  const toolTip = useTooltip({
    placement: 'bottom-start',
    offset: -1,
    trigger: 'click',
    enableDismiss: false,
  });

  const showFeedbackPopup = parsedCallCount === randomCallCount && establishedCalls?.length === 0;

  useEffect(() => {
    if (showFeedbackPopup || isFeedbackOpened) {
      setTimeout(() => {
        toolTip.setOpen(true);
        setIsFeedbackOpen(false);
        setRandomCallCount(Math.round(Math.random() * (MAX_RANGE - MIN_RANGE) + MIN_RANGE));
        localStorage.setItem('softphone.call-count', JSON.stringify(0));
      }, DEFAULT_ASK_FOR_FEEDBACK_TIMEOUT);

      dismissTimeoutRef.current = setTimeout(() => {
        toolTip.setOpen(false);
      }, DEFAULT_DISMISS_FEEDBACK_TIMEOUT);
    }

    if (clearDismissTimeout && dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      setClearDismissTimeout(false);
      dismissTimeoutRef.current = null;
    }
  }, [showFeedbackPopup, isFeedbackOpened, clearDismissTimeout]);

  return (
    <ErrorBoundary
      tier='feature'
      feature='softphone'
      team='fe-phone-config'
      fallback={({ error }) => (
        <>
          <Wrapper styled={allowButtonView} setReference={toolTip.refs.setReference}>
            {showSoftphoneIcon && (
              <SoftphoneIconButton
                isSoftphoneActive={isSoftphoneActive}
                error={error instanceof Error ? error : new Error('Unknown error has occurred!')}
              />
            )}
            {allowPopout && isOpen && (
              <SoftphonePopout
                error={error instanceof Error ? error : new Error('unknown error occurred!')}
                isLoading={isLoading}
              />
            )}
          </Wrapper>
        </>
      )}
    >
      <Wrapper styled={allowButtonView} setReference={toolTip.refs.setReference}>
        {showSoftphoneIcon && <SoftphoneIconButton isSoftphoneActive={isSoftphoneActive} error={providersError} />}
        {showSoftphoneIcon && (
          <SoftphoneFeedbackCallRating setClearDismissTimeout={setClearDismissTimeout} {...toolTip} />
        )}
        {allowCallBarToShow && isConnected && (
          <CallBars
            showIncomingCallsUI={showIncomingCallsUI}
            hasActiveCalls={showActiveCallBar}
            incomingCalls={incomingCalls}
            isMobile={isMobile}
          />
        )}
        {allowPopout && (!providersError || providersError) && (
          <SoftphonePopout error={providersError} isLoading={isLoading} />
        )}
      </Wrapper>
    </ErrorBoundary>
  );
});

SoftphoneWrap.displayName = 'SoftphoneWrap';

const Wrapper = ({
  styled,
  children,
  setReference,
}: {
  styled: boolean;
  children: React.ReactNode;
  setReference: (node: ReferenceType | null) => void;
}) => {
  if (!styled) {
    return (
      <div
        ref={setReference}
        css={css`
          position: relative;
        `}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      ref={setReference}
      css={css`
        height: 100%;
        display: flex;
        align-items: center;
        position: relative;
        padding: ${theme.spacing(0.5)};
        gap: ${theme.spacing(1)};
      `}
    >
      {children}
    </div>
  );
};
